import { ComponentType, ReactElement, ReactNode } from 'react';

import { observer } from 'mobx-react-lite';

import { ApplicantCardTags } from '@components/applicant-card';
import { Badge, BadgeVariant } from '@components/badge';
import { IconAppConnect } from '@components/icons/app-connect';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconReject } from '@components/icons/reject';
import { IconTeam } from '@components/icons/team';

import { useIntegrationLabels } from 'hooks/use-integration-labels';
import { TranslationKey, useTranslate } from 'locale';
import { IApplication } from 'models/types';

type ApplicationLabelName = 'sourced' | 'notified' | 'new' | 'manuallyadded';

type LabelBadge = {
  variant: BadgeVariant;
  icon?: ReactElement;
  textKey: TranslationKey;
};

const labelBadges: { [name in ApplicationLabelName]: LabelBadge } = {
  new: { variant: 'green', textKey: 'application.label.new' },
  sourced: { variant: 'blue', icon: <IconTeam />, textKey: 'application.label.sourced' },
  manuallyadded: {
    variant: 'purple',
    icon: <IconPersonAdd />,
    textKey: 'application.label.manuallyadded',
  },
  notified: { variant: 'red', icon: <IconReject />, textKey: 'application.label.notified' },
};

const labelNames = Object.keys(labelBadges) as Array<ApplicationLabelName>;

const hasLabel: { [name in ApplicationLabelName]: (application: IApplication) => boolean } = {
  sourced: (application) => Boolean(application.referredBy),
  notified: (application) => Boolean(application.isNotifiedOfRejection),
  new: (application) => Boolean(application.userRead && !application.userRead.newApplication),
  manuallyadded: (application) => Boolean(application.isManuallyAdded),
};

function isApplicationLabelName(name: string): name is ApplicationLabelName {
  return labelNames.includes(name as ApplicationLabelName);
}

// The index stamps a `none` pseudo-label on unlabelled applicants.
export function hasSearchLabels(labels: Array<{ name: string }>) {
  return labels.some((label) => isApplicationLabelName(label.name));
}

function useLabelBadges(names: Array<ApplicationLabelName>) {
  const { t } = useTranslate();

  return labelNames
    .filter((name) => names.includes(name))
    .map((name) => {
      const { variant, icon, textKey } = labelBadges[name];
      return <Badge key={name} variant={variant} icon={icon} text={t(textKey)} />;
    });
}

type ApplicationLabelsProps = {
  application: IApplication;
  wrapper?: ComponentType<{ children: ReactNode }>;
};

type SearchApplicationLabelsProps = {
  labels: Array<{ name: string }>;
};

export const ApplicationLabels = observer<ApplicationLabelsProps>(function ApplicationLabels({
  application,
  wrapper: Wrapper = ApplicantCardTags,
}) {
  const integrationsLabels = useIntegrationLabels(application.integrationsResults);

  const badges = useLabelBadges(labelNames.filter((name) => hasLabel[name](application)));

  return (
    <Wrapper>
      {badges}
      {integrationsLabels?.map((label) => (
        <Badge
          key={label.text}
          variant={label.variant}
          icon={<IconAppConnect />}
          text={label.text}
        />
      ))}
    </Wrapper>
  );
});

export function SearchApplicationLabels({ labels }: SearchApplicationLabelsProps) {
  const badges = useLabelBadges(labels.map((label) => label.name).filter(isApplicationLabelName));

  return <ApplicantCardTags>{badges}</ApplicantCardTags>;
}
